/**
 * CustomerCard
 * Consolidated customer info, selector, status, SMS, and other assignments
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import {
  User,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Check,
  MessageSquare,
  Send,
  ArrowUpRight,
  ArrowDownLeft,
  FileText
} from 'lucide-react'
import type {
  CustomerCommStatus,
  CustomerInvoiceStatus,
  ContactMethod,
  TgWorkStatus
} from '../../types/statusTypes'
import { CustomerCancellationModal, type CancellationResult } from '../dialogs/CustomerCancellationModal'
import {
  CUSTOMER_COMM_STATUSES,
  getCustomerCommStatusBadgeClasses,
  getCustomerCommStatusName,
  getCustomerInvoiceStatusBadgeClasses,
  getCustomerInvoiceStatusName,
  getContactMethodLabel,
  canInvoiceCustomer
} from '../../services/statusSystemV2'
import { supabase } from '../../lib/supabase'
import { SendSMSModal } from '../SendSMSModal'

interface Customer {
  id: string
  full_name: string
  phone: string | null
  email: string | null
  address_street: string | null
  address_city: string | null
}

interface OtherAssignment {
  id: string
  title: string
  status: string
  start_date: string | null
}

interface SMSMessage {
  id: string
  message: string
  direction: 'outbound' | 'inbound'
  status: string
  sender_name: string | null
  created_at: string
}

interface TgInfo {
  id: string
  subcontractorId: string
  name: string
  phone: string | null
  currentStatus: TgWorkStatus
}

interface CustomerCardProps {
  // Customer data
  customerId: string | null
  customers: Customer[]

  // Assignment context
  assignmentId?: string
  assignmentNumber?: string
  assignmentTitle?: string
  assignmentDate?: string
  assignmentAddress?: string
  startTime?: string | null

  // Status data (V2)
  commStatus: CustomerCommStatus
  invoiceStatus: CustomerInvoiceStatus
  informedAt?: string | null
  informedMethod?: ContactMethod | null
  confirmedAt?: string | null
  confirmedMethod?: ContactMethod | null
  cancelledAt?: string | null
  cancelledReason?: string | null
  invoiceNumber?: string | null
  invoiceDate?: string | null

  // Assignment status for validation
  assignmentStatus: string

  // Is this a new assignment?
  isNewAssignment: boolean

  // Other assignments for this customer
  otherAssignments?: OtherAssignment[]

  // TG list for cancellation modal
  tgList?: TgInfo[]

  // Callbacks
  onCustomerChange: (customerId: string) => void
  onCustomerClear: () => void
  onCommStatusChange: (status: CustomerCommStatus, method?: ContactMethod) => void
  onRegisterInvoice: (invoiceNumber: string, invoiceDate: string) => void
  onCancellation?: (result: CancellationResult) => Promise<void>

  // State
  isLocked?: boolean
  validationError?: boolean

  // Collapse props
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}

export function CustomerCard({
  customerId,
  customers,
  assignmentId,
  assignmentNumber,
  assignmentTitle,
  assignmentDate,
  assignmentAddress,
  startTime,
  commStatus,
  invoiceStatus,
  informedAt,
  informedMethod,
  confirmedAt,
  confirmedMethod,
  cancelledAt,
  cancelledReason,
  invoiceNumber,
  invoiceDate,
  assignmentStatus,
  isNewAssignment,
  otherAssignments = [],
  tgList = [],
  onCustomerChange,
  onCommStatusChange,
  onRegisterInvoice,
  onCancellation,
  isLocked = false,
  validationError = false,
  isCollapsed = false,
  onToggleCollapse
}: CustomerCardProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Customer selector state
  const [customerSearch, setCustomerSearch] = useState('')
  const [showCustomerSelector, setShowCustomerSelector] = useState(false)

  // Status dropdown state
  const [showCommDropdown, setShowCommDropdown] = useState(false)
  const [showMethodSelector, setShowMethodSelector] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<CustomerCommStatus | null>(null)
  const [showCancellationDialog, setShowCancellationDialog] = useState(false)

  // Invoice modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [invoiceNumberInput, setInvoiceNumberInput] = useState('')
  const [invoiceDateInput, setInvoiceDateInput] = useState(new Date().toISOString().split('T')[0])

  // Collapsible sections - collapsed by default
  const [smsExpanded, setSmsExpanded] = useState(false)
  const [showAllSms, setShowAllSms] = useState(false)
  const [showSendModal, setShowSendModal] = useState(false)

  // Customer change confirmation
  const [pendingCustomerChange, setPendingCustomerChange] = useState<string | null>(null)

  // Refs
  const customerDropdownRef = useRef<HTMLDivElement>(null)
  const commDropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const selectedCustomer = customers.find(c => c.id === customerId)
  const canInvoice = canInvoiceCustomer(assignmentStatus as any)

  // Disable cancellation once work has been reported
  const workHasBeenReported = ['rapporterad', 'granskning', 'attesterad', 'slutford'].includes(assignmentStatus)

  // Fetch SMS messages
  const { data: smsMessages = [], refetch: refetchSms } = useQuery({
    queryKey: ['customer-sms', assignmentId, customerId, showAllSms],
    queryFn: async () => {
      if (!customerId) return []

      let query = supabase
        .from('communications')
        .select('id, message, direction, status, sender_name, created_at')
        .eq('recipient_id', customerId)
        .eq('recipient_type', 'customer')
        .order('created_at', { ascending: false })

      if (!showAllSms && assignmentId) {
        query = query.eq('assignment_id', assignmentId)
      }

      const { data, error } = await query.limit(50)
      if (error) return []
      return data as SMSMessage[]
    },
    enabled: !!customerId && !isNewAssignment
  })

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerSelector(false)
        setCustomerSearch('')
      }
      if (commDropdownRef.current && !commDropdownRef.current.contains(event.target as Node)) {
        setShowCommDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (showCustomerSelector && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [showCustomerSelector])

  const handleCommStatusSelect = (status: CustomerCommStatus) => {
    if (status === 'avbokad') {
      setShowCancellationDialog(true)
      setShowCommDropdown(false)
    } else if (status === 'informerad' || status === 'bekraftad') {
      setPendingStatus(status)
      setShowMethodSelector(true)
      setShowCommDropdown(false)
    } else {
      onCommStatusChange(status)
      setShowCommDropdown(false)
    }
  }

  const handleMethodSelect = (method: ContactMethod) => {
    if (pendingStatus) {
      onCommStatusChange(pendingStatus, method)
      setPendingStatus(null)
      setShowMethodSelector(false)
    }
  }

  const handleCancellationConfirm = async (result: CancellationResult) => {
    if (onCancellation) {
      await onCancellation(result)
    }
    setShowCancellationDialog(false)
  }

  const handleInvoiceSubmit = () => {
    if (!invoiceNumberInput.trim() || !invoiceDateInput) return
    onRegisterInvoice(invoiceNumberInput.trim(), invoiceDateInput)
    setShowInvoiceModal(false)
    setInvoiceNumberInput('')
    setInvoiceDateInput(new Date().toISOString().split('T')[0])
  }

  // Statuses where changing customer is risky
  const dangerousStatuses = ['planerad', 'pagaende', 'rapporterad', 'granskning', 'attesterad', 'slutford', 'fakturerad']
  const isStatusDangerous = dangerousStatuses.includes(assignmentStatus)

  const getCustomerChangeWarning = () => {
    switch (assignmentStatus) {
      case 'fakturerad':
        return 'Uppdraget är redan fakturerat! Att byta kund kan skapa problem med bokföring och kundfaktura.'
      case 'slutford':
        return 'Uppdraget är slutfört. Att byta kund påverkar historik och rapporter.'
      case 'attesterad':
        return 'Uppdraget har attesterats. Kundbyte kan påverka fakturering.'
      case 'granskning':
      case 'rapporterad':
        return 'Arbete har rapporterats på detta uppdrag. Är du säker på att du vill byta kund?'
      case 'pagaende':
        return 'Arbete pågår på detta uppdrag. Kundbyte kan påverka pågående arbete.'
      case 'planerad':
        return 'Uppdraget är planerat med trädgårdsmästare. Kundbyte kan skapa förvirring.'
      default:
        return ''
    }
  }

  const handleCustomerSelect = (newCustomerId: string) => {
    // If status is dangerous and we already have a customer, show warning
    if (isStatusDangerous && customerId && customerId !== newCustomerId) {
      setPendingCustomerChange(newCustomerId)
      setShowCustomerSelector(false)
      return
    }

    onCustomerChange(newCustomerId)
    setCustomerSearch('')
    setShowCustomerSelector(false)
  }

  const confirmCustomerChange = () => {
    if (pendingCustomerChange) {
      onCustomerChange(pendingCustomerChange)
      setPendingCustomerChange(null)
      setCustomerSearch('')
    }
  }

  const handleMessageSent = () => {
    refetchSms()
    queryClient.invalidateQueries({ queryKey: ['customer-sms'] })
  }

  const contactMethods: { value: ContactMethod; label: string; icon: React.ReactNode }[] = [
    { value: 'phone', label: 'Telefon', icon: <Phone className="h-4 w-4" /> },
    { value: 'sms', label: 'SMS', icon: <MessageSquare className="h-4 w-4" /> },
    { value: 'email', label: 'E-post', icon: <Mail className="h-4 w-4" /> },
    { value: 'in_person', label: 'Personligt', icon: <User className="h-4 w-4" /> }
  ]

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return null
    try {
      return format(new Date(timestamp), 'yyyy-MM-dd HH:mm', { locale: sv })
    } catch {
      return null
    }
  }

  const fullAddress = selectedCustomer
    ? [selectedCustomer.address_street, selectedCustomer.address_city].filter(Boolean).join(', ')
    : null

  // Search on both name and address
  const filteredCustomers = customers.filter(c => {
    const search = customerSearch.toLowerCase()
    const nameMatch = c.full_name.toLowerCase().includes(search)
    const addressMatch = c.address_street?.toLowerCase().includes(search) || false
    const cityMatch = c.address_city?.toLowerCase().includes(search) || false
    return nameMatch || addressMatch || cityMatch
  })

  return (
    <div className={`bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border ${validationError ? 'border-red-500' : 'border-nature-200 dark:border-nature-700'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 ${!isCollapsed ? 'border-b border-nature-200 dark:border-nature-700' : ''}`}>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <User className="h-4 w-4 text-nature-500" />
          <h3 className="text-base font-semibold text-nature-900 dark:text-nature-100">
            Kund
          </h3>
          {selectedCustomer ? (
            <span className="text-sm text-nature-500 dark:text-nature-400">
              {selectedCustomer.full_name}
            </span>
          ) : (
            <span className="text-red-500 text-sm">*</span>
          )}
        </button>
        <div className="flex items-center gap-2">
          {selectedCustomer && (
            <>
              {!isLocked && (
                <div className="relative" ref={customerDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowCustomerSelector(!showCustomerSelector)}
                    className="text-sm text-nature-500 hover:text-nature-700 dark:hover:text-nature-300 flex items-center gap-1"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Byt
                  </button>

                  {/* Customer selector dropdown */}
                  {showCustomerSelector && (
                    <div className="absolute z-50 mt-2 right-0 w-72 bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700">
                      <div className="p-2 border-b border-nature-200 dark:border-nature-700">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
                          <input
                            ref={searchInputRef}
                            type="text"
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            placeholder="Sök namn eller adress..."
                            className="input-field pl-9 py-2 text-sm"
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {filteredCustomers.map(customer => {
                          const customerAddress = [customer.address_street, customer.address_city].filter(Boolean).join(', ')
                          return (
                            <button
                              key={customer.id}
                              type="button"
                              onClick={() => handleCustomerSelect(customer.id)}
                              className={`w-full px-4 py-2 text-left hover:bg-nature-100 dark:hover:bg-nature-700 flex items-center justify-between ${
                                customerId === customer.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                              }`}
                            >
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-nature-900 dark:text-nature-100">{customer.full_name}</div>
                                {customerAddress && (
                                  <div className="text-xs text-nature-500 dark:text-nature-400 truncate">{customerAddress}</div>
                                )}
                              </div>
                              {customerId === customer.id && <Check className="h-4 w-4 text-primary-600 flex-shrink-0 ml-2" />}
                            </button>
                          )
                        })}
                        {filteredCustomers.length === 0 && (
                          <p className="px-4 py-3 text-nature-500 text-sm text-center">Ingen kund hittades</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={() => navigate(`/admin/customers/${selectedCustomer.id}`)}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Detaljer
              </button>
            </>
          )}
          {/* Collapse toggle */}
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="p-1 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300"
            >
              {isCollapsed ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Content - only show when not collapsed */}
      {!isCollapsed && (
      <>
      <div className="p-4">
        {selectedCustomer ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left: Customer Info */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-nature-400 flex-shrink-0" />
                <span className="text-sm font-medium text-nature-900 dark:text-nature-100">
                  {selectedCustomer.full_name}
                </span>
              </div>

              {selectedCustomer.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-nature-400 flex-shrink-0" />
                  <a
                    href={`tel:${selectedCustomer.phone}`}
                    className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    {selectedCustomer.phone}
                  </a>
                </div>
              )}

              {selectedCustomer.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-nature-400 flex-shrink-0" />
                  <a
                    href={`mailto:${selectedCustomer.email}`}
                    className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 truncate"
                  >
                    {selectedCustomer.email}
                  </a>
                </div>
              )}

              {fullAddress && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-nature-400 flex-shrink-0" />
                  <span className="text-sm text-nature-700 dark:text-nature-300">
                    {fullAddress}
                  </span>
                </div>
              )}
            </div>

            {/* Right: Status (only for existing assignments) */}
            {!isNewAssignment && (
              <div className="space-y-3">
                {/* Communication Status Dropdown */}
                <div className="relative" ref={commDropdownRef}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-nature-500 dark:text-nature-400 uppercase">
                      Kommunikation
                    </span>
                    <button
                      type="button"
                      onClick={() => !isLocked && setShowCommDropdown(!showCommDropdown)}
                      disabled={isLocked}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getCustomerCommStatusBadgeClasses(commStatus)} ${
                        !isLocked ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
                      }`}
                    >
                      {getCustomerCommStatusName(commStatus)}
                      {!isLocked && <ChevronDown className="h-3 w-3" />}
                    </button>
                  </div>

                  {showCommDropdown && (
                    <div className="absolute z-50 mt-1 right-0 bg-white dark:bg-nature-800 border border-nature-200 dark:border-nature-700 rounded-lg shadow-lg min-w-[180px]">
                      {CUSTOMER_COMM_STATUSES.filter(s => s.key !== 'avbokad').map((status) => (
                        <button
                          key={status.key}
                          type="button"
                          onClick={() => handleCommStatusSelect(status.key)}
                          className={`w-full px-3 py-2 text-left hover:bg-nature-50 dark:hover:bg-nature-700 flex items-center gap-2 text-sm ${
                            commStatus === status.key ? 'bg-nature-50 dark:bg-nature-700' : ''
                          }`}
                        >
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${status.bgColor}`}>
                            {status.name}
                          </span>
                          {commStatus === status.key && (
                            <Check className="h-3 w-3 text-green-600 ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {(informedAt || confirmedAt) && (
                    <div className="mt-1 text-xs text-nature-400">
                      {confirmedAt ? (
                        <span>Bekräftad {formatTimestamp(confirmedAt)} {confirmedMethod && `(${getContactMethodLabel(confirmedMethod)})`}</span>
                      ) : informedAt ? (
                        <span>Informerad {formatTimestamp(informedAt)} {informedMethod && `(${getContactMethodLabel(informedMethod)})`}</span>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* Invoice Status Section */}
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-nature-500 dark:text-nature-400 uppercase">
                      Fakturering
                    </span>
                    <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${getCustomerInvoiceStatusBadgeClasses(invoiceStatus)}`}>
                      {getCustomerInvoiceStatusName(invoiceStatus)}
                    </span>
                  </div>

                  {invoiceStatus === 'fakturerad' ? (
                    // Show invoice info when invoiced
                    <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-xs">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                        <FileText className="h-3.5 w-3.5" />
                        <span className="font-medium">{invoiceNumber || 'Faktura'}</span>
                      </div>
                      {invoiceDate && (
                        <div className="text-green-600 dark:text-green-400 mt-1">
                          Fakturadatum: {invoiceDate}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Show button to register invoice (if allowed)
                    <>
                      {canInvoice && !isLocked ? (
                        <button
                          type="button"
                          onClick={() => setShowInvoiceModal(true)}
                          className="mt-2 w-full text-xs px-3 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors flex items-center justify-center gap-1"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          Registrera faktura
                        </button>
                      ) : (
                        <div className="mt-1 text-xs text-nature-400">
                          {!canInvoice ? 'Attestering krävs' : 'Låst'}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Cancellation button/status */}
                {commStatus === 'avbokad' ? (
                  <div className="pt-2 border-t border-nature-200 dark:border-nature-700">
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                      <span className="text-xs font-medium uppercase">Avbokad</span>
                      {cancelledAt && (
                        <span className="text-xs text-nature-400">{formatTimestamp(cancelledAt)}</span>
                      )}
                    </div>
                    {cancelledReason && (
                      <p className="text-xs text-nature-500 italic mt-1">"{cancelledReason}"</p>
                    )}
                  </div>
                ) : (
                  /* Only show cancellation for 'planerad' or 'pagaende' status, and no work reported */
                  !isLocked && !workHasBeenReported && (assignmentStatus === 'planerad' || assignmentStatus === 'pagaende') && (
                    <button
                      type="button"
                      onClick={() => setShowCancellationDialog(true)}
                      className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Registrera avbokning
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        ) : (
          /* No customer selected - show search */
          <div className="relative" ref={customerDropdownRef}>
            <div className="text-center py-2 mb-3">
              <User className="h-8 w-8 mx-auto text-nature-300 dark:text-nature-600 mb-2" />
              <p className="text-nature-500 dark:text-nature-400 text-sm">Ingen kund vald</p>
            </div>
            {!isLocked && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value)
                      setShowCustomerSelector(true)
                    }}
                    onFocus={() => setShowCustomerSelector(true)}
                    placeholder="Sök namn eller adress..."
                    className={`input-field pl-9 ${validationError ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                  />
                </div>
                {showCustomerSelector && (
                  <div className="absolute z-50 mt-1 left-4 right-4 bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 max-h-64 overflow-y-auto">
                    {filteredCustomers.map(customer => {
                      const customerAddress = [customer.address_street, customer.address_city].filter(Boolean).join(', ')
                      return (
                        <button
                          key={customer.id}
                          type="button"
                          onClick={() => handleCustomerSelect(customer.id)}
                          className="w-full px-4 py-2 text-left hover:bg-nature-100 dark:hover:bg-nature-700"
                        >
                          <div className="text-sm font-medium text-nature-900 dark:text-nature-100">{customer.full_name}</div>
                          {customerAddress && (
                            <div className="text-xs text-nature-500 dark:text-nature-400">{customerAddress}</div>
                          )}
                        </button>
                      )
                    })}
                    {filteredCustomers.length === 0 && (
                      <p className="px-4 py-3 text-nature-500 text-sm text-center">Ingen kund hittades</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* SMS Section - Collapsible */}
      {selectedCustomer && !isNewAssignment && (
        <div className="border-t border-nature-200 dark:border-nature-700">
          <button
            type="button"
            onClick={() => setSmsExpanded(!smsExpanded)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-nature-500" />
              <span className="text-sm font-medium text-nature-700 dark:text-nature-300">SMS</span>
              {smsMessages.length > 0 && (
                <span className="bg-nature-200 dark:bg-nature-600 text-nature-600 dark:text-nature-300 px-1.5 py-0.5 rounded text-xs">
                  {smsMessages.length}
                </span>
              )}
            </div>
            {smsExpanded ? (
              <ChevronUp className="h-4 w-4 text-nature-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-nature-400" />
            )}
          </button>

          {smsExpanded && (
            <div className="px-4 pb-4">
              {/* SMS controls */}
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={() => setShowAllSms(!showAllSms)}
                  className={`text-xs px-2 py-1 rounded transition-colors ${
                    showAllSms
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-nature-500 hover:text-nature-700 dark:hover:text-nature-300'
                  }`}
                >
                  {showAllSms ? 'Alla SMS' : 'Detta uppdrag'}
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => refetchSms()}
                    className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 p-1"
                    title="Uppdatera"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  {selectedCustomer.phone && (
                    <button
                      type="button"
                      onClick={() => setShowSendModal(true)}
                      className="btn-primary text-xs flex items-center gap-1 py-1.5"
                    >
                      <Send className="h-3 w-3" />
                      Skicka SMS
                    </button>
                  )}
                </div>
              </div>

              {/* SMS messages */}
              {smsMessages.length === 0 ? (
                <p className="text-sm text-nature-400 text-center py-3">
                  {showAllSms ? 'Inga SMS till denna kund' : 'Inga SMS för detta uppdrag'}
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {smsMessages.slice(0, 5).map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-2 rounded text-xs ${
                        msg.direction === 'outbound'
                          ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
                          : 'bg-nature-50 dark:bg-nature-700 border border-nature-200 dark:border-nature-600'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          {msg.direction === 'outbound' ? (
                            <ArrowUpRight className="h-3 w-3 text-primary-500" />
                          ) : (
                            <ArrowDownLeft className="h-3 w-3 text-green-500" />
                          )}
                          <span className="text-nature-500">
                            {msg.direction === 'outbound' ? (msg.sender_name || 'Skickat') : 'Svar'}
                          </span>
                        </div>
                        <span className="text-nature-400">
                          {format(new Date(msg.created_at), 'MM-dd HH:mm')}
                        </span>
                      </div>
                      <p className="text-nature-700 dark:text-nature-300 line-clamp-2">{msg.message}</p>
                    </div>
                  ))}
                  {smsMessages.length > 5 && (
                    <p className="text-xs text-nature-400 text-center">+ {smsMessages.length - 5} fler meddelanden</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* All Customer Assignments - Link to filtered assignment list */}
      {selectedCustomer && otherAssignments.length > 0 && (
        <div className="border-t border-nature-200 dark:border-nature-700">
          <button
            type="button"
            onClick={() => navigate(`/admin/assignments?customer=${customerId}`)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors group"
          >
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-nature-500" />
              <span className="text-sm font-medium text-nature-700 dark:text-nature-300">Alla uppdrag</span>
              <span className="bg-nature-200 dark:bg-nature-600 text-nature-600 dark:text-nature-300 px-1.5 py-0.5 rounded text-xs">
                {otherAssignments.length + 1}
              </span>
            </div>
            <ExternalLink className="h-4 w-4 text-nature-400 group-hover:text-primary-500 transition-colors" />
          </button>
        </div>
      )}
      </>
      )}

      {/* Contact method selector modal */}
      {showMethodSelector && pendingStatus && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-sm w-full p-4">
            <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-4">
              Hur kontaktades kunden?
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {contactMethods.map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => handleMethodSelect(method.value)}
                  className="flex items-center gap-2 px-4 py-3 border border-nature-200 dark:border-nature-600 rounded-lg hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
                >
                  {method.icon}
                  <span className="text-nature-900 dark:text-nature-100">{method.label}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setPendingStatus(null)
                setShowMethodSelector(false)
              }}
              className="mt-4 w-full px-4 py-2 text-nature-500 hover:text-nature-700 dark:hover:text-nature-300"
            >
              Avbryt
            </button>
          </div>
        </div>
      )}

      {/* Cancellation modal */}
      <CustomerCancellationModal
        isOpen={showCancellationDialog}
        onClose={() => setShowCancellationDialog(false)}
        assignmentNumber={assignmentNumber}
        assignmentTitle={assignmentTitle || ''}
        assignmentDate={assignmentDate || ''}
        assignmentAddress={assignmentAddress}
        startTime={startTime}
        customerName={selectedCustomer?.full_name || ''}
        tgList={tgList}
        onConfirm={handleCancellationConfirm}
      />

      {/* Customer change confirmation dialog */}
      {pendingCustomerChange && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <User className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                Bekräfta kundbyte
              </h3>
            </div>
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {getCustomerChangeWarning()}
              </p>
            </div>
            <p className="text-sm text-nature-600 dark:text-nature-400 mb-4">
              Från: <span className="font-medium">{selectedCustomer?.full_name || 'Ingen kund'}</span>
              <br />
              Till: <span className="font-medium">{customers.find(c => c.id === pendingCustomerChange)?.full_name}</span>
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPendingCustomerChange(null)}
                className="flex-1 px-4 py-2 border border-nature-300 dark:border-nature-600 rounded-lg text-nature-700 dark:text-nature-300 hover:bg-nature-50 dark:hover:bg-nature-700"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={confirmCustomerChange}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Byt kund ändå
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Registration Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-4">
            <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-4">
              Registrera kundfaktura
            </h3>
            <p className="text-sm text-amber-600 dark:text-amber-400 mb-4 p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
              OBS: Fakturering kan inte ångras efter registrering.
            </p>
            <div className="space-y-4 mb-4">
              <div>
                <label className="form-label">Fakturanummer *</label>
                <input
                  type="text"
                  value={invoiceNumberInput}
                  onChange={(e) => setInvoiceNumberInput(e.target.value)}
                  placeholder="t.ex. F2025-001"
                  className="input-field"
                  autoFocus
                />
              </div>
              <div>
                <label className="form-label">Fakturadatum *</label>
                <input
                  type="date"
                  value={invoiceDateInput}
                  onChange={(e) => setInvoiceDateInput(e.target.value)}
                  className="input-field"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowInvoiceModal(false)
                  setInvoiceNumberInput('')
                  setInvoiceDateInput(new Date().toISOString().split('T')[0])
                }}
                className="flex-1 px-4 py-2 border border-nature-300 dark:border-nature-600 rounded-lg text-nature-700 dark:text-nature-300 hover:bg-nature-50 dark:hover:bg-nature-700"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={handleInvoiceSubmit}
                disabled={!invoiceNumberInput.trim() || !invoiceDateInput}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Registrera faktura
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send SMS Modal */}
      {showSendModal && selectedCustomer?.phone && assignmentId && (
        <SendSMSModal
          isOpen={showSendModal}
          onClose={() => setShowSendModal(false)}
          recipientType="customer"
          recipientId={customerId!}
          recipientName={selectedCustomer.full_name}
          recipientPhone={selectedCustomer.phone}
          assignmentId={assignmentId}
          onMessageSent={handleMessageSent}
        />
      )}
    </div>
  )
}
